import { AutocompleteInputChangeReason } from '@material-ui/core';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { customsTaxService } from '@services/requests/customsTaxService';
import { CodeItem } from '@services/requests/customsTaxService/interface';
import { useDebounceStream } from '@settings/core/stateContexts/useDebounceStream';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { checkErrorExisting } from '../helpers/helpers';
import { CustomsPaymentsCodeSelectProps } from './types';

function CustomsPaymentsCodeSelect({
  errors,
  codeDescription,
  onChangeCode,
  onResetFields,
  onResetTaxParams,
}: CustomsPaymentsCodeSelectProps) {
  const { t } = useTranslation(['CustomsPayments']);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<CodeItem[]>([]);
  const [debounceValue, value, setDebounceValue] = useDebounceStream<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const inputValue = codeDescription.length > 0 ? `${value} - ${codeDescription}` : value;

  const openDropdownHandler = useCallback(() => {
    setOpen(true);
  }, []);

  const closeDropdownHandler = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    setOptions([]);

    if (debounceValue.length < 3) {
      closeDropdownHandler();
      return;
    }

    const fetchCodes = async (searchTerm: string) => {
      const result = await customsTaxService().LoadCodesBySearch(searchTerm);

      if (!result) {
        setOptions([]);
        setIsLoading(false);
        return;
      }

      const isNotSingleResult = Array.isArray(result);

      setOptions(isNotSingleResult ? result : [result]);
      setIsLoading(false);
    };

    setIsLoading(true);
    fetchCodes(debounceValue);
  }, [debounceValue, closeDropdownHandler]);

  const selectCodeHandler = async (newValue: CodeItem) => {
    if (!newValue) {
      onResetFields();
      onResetTaxParams();
      onChangeCode('code', '');
      onChangeCode('codeDescription', '');
      return;
    }

    const { tncode, g, descr } = newValue;

    const isCategory = tncode.includes('...');

    if (isCategory) {
      setIsLoading(true);
      const [searchCode] = g.split(':');

      const result = await customsTaxService().LoadCodesBySearchCode(searchCode);

      if (!result) {
        setOptions([]);
        setIsLoading(false);
        return;
      }

      setOptions(Array.isArray(result) ? result : [result]);
      setIsLoading(false);
      return;
    }

    const formattedCode = tncode.replaceAll(/\s/g, '');
    setDebounceValue(formattedCode);
    onResetFields();
    onResetTaxParams();
    onChangeCode('code', formattedCode);
    onChangeCode('codeDescription', descr);
    closeDropdownHandler();
  };

  const changeInputHandler = (newValue: string, reason: AutocompleteInputChangeReason) => {
    if (newValue === '' && reason === 'reset') {
      return;
    }

    const isSelectValue = newValue.includes('-');
    const isLessThenCurrentValue = inputValue.length > newValue.length;

    let formattedValue = newValue.match(/\d+/g)?.join() || newValue;

    if (isSelectValue) {
      formattedValue = newValue
        .match(/\d+.*?(?=-)/)
        .join()
        .replaceAll(' ', '');
    }

    if (formattedValue.length < 3) {
      closeDropdownHandler();
    }

    if (isSelectValue && isLessThenCurrentValue) {
      onChangeCode('codeDescription', '');
      onChangeCode('code', '');
      setDebounceValue('');
    }

    if (value === formattedValue) {
      return;
    }

    onResetFields();
    onResetTaxParams();
    onChangeCode('code', formattedValue.replace(/,/g, ''));

    if (reason !== 'reset') {
      setDebounceValue(formattedValue.replace(/,/g, ''));
    }
  };

  return (
    <Autocomplete
      id="code-tnved"
      className="code-tnved"
      fullWidth
      popupIcon={<></>}
      open={open && debounceValue.length >= 3}
      onOpen={openDropdownHandler}
      onClose={closeDropdownHandler}
      onReset={() => {
        onResetFields();
        onResetTaxParams();
      }}
      loadingText="Загрузка..."
      noOptionsText="Ничего не найдено"
      inputValue={inputValue}
      onInputChange={(_, value, reason) => changeInputHandler(value, reason)}
      filterOptions={(x) => x}
      openOnFocus={false}
      isOptionEqualToValue={(option, value) => {
        return option.g === value.g;
      }}
      getOptionLabel={(option) => `${option.tncode} - ${option.descr}`}
      onChange={(_, newValue) => selectCodeHandler(newValue)}
      disableCloseOnSelect
      options={options}
      loading={isLoading}
      renderInput={(params) => (
        <TextField
          {...params}
          error={checkErrorExisting(['code', 'codeDescription'], errors)}
          label={t('CustomsPayments:HSCodeOrProductNamePlaceholder')}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
}

export { CustomsPaymentsCodeSelect };
